"use client";

import type { ReactNode } from "react";
// See the note in `arrow-button.tsx`: a locale-less path resolves to the
// default locale, which this vertical 404s.
import { Link } from "@/i18n/routing";
import { EJHEALTH_COLORS, EJHEALTH_TYPE } from "@/lib/theme/ej-health";
import { ejHealthCategoryRoute, ejHealthTagRoute } from "@/lib/routes";
import { categorySlugForTag } from "@/fixtures/ej-health/category/fixtures";

/** Circular outlined social icon button — fills with primary on hover. */
export function SocialIconButton({
  ariaLabel,
  href = "#",
  children,
}: {
  ariaLabel: string;
  href?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      aria-label={ariaLabel}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors"
      style={{
        border: `1px solid ${EJHEALTH_COLORS.dividerStrong}`,
        color: EJHEALTH_COLORS.dark,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = EJHEALTH_COLORS.primary;
        e.currentTarget.style.borderColor = EJHEALTH_COLORS.primary;
        e.currentTarget.style.color = EJHEALTH_COLORS.textOnDark;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.borderColor = EJHEALTH_COLORS.dividerStrong;
        e.currentTarget.style.color = EJHEALTH_COLORS.dark;
      }}
    >
      {children}
    </a>
  );
}

const TAG_PILL_CLASS = "inline-flex items-center px-4 py-2 transition-colors";

const tagPillStyle = {
  ...EJHEALTH_TYPE.meta,
  backgroundColor: EJHEALTH_COLORS.primarySoft,
  color: EJHEALTH_COLORS.primary,
  border: `1px solid ${EJHEALTH_COLORS.primary}`,
  letterSpacing: "0.06em",
} as const;

/**
 * A tag cloud where every pill that looks like a link is one.
 *
 * Each pill used to be a `TagPill` with no `href` at all, falling through to a
 * `"#"` default — so all 30-odd of them were dead, and `globals.css` gates the
 * pointer affordance behind `:not([href="#"])`, which meant they did not even
 * respond to the pointer.
 *
 * A pill that NAMES one of the site's categories (「醫學通識」, 「食出健康」)
 * opens that category listing; every other pill opens its tag page, which
 * resolves for any tag. The plain-label fallback is gone with it — it existed
 * only because article-level tags like 「陳俊平醫生」 had nowhere to go.
 */
export function TagCloud({
  tags,
  className = "flex flex-wrap gap-2.5",
}: {
  tags: string[];
  className?: string;
}) {
  return (
    <div className={className}>
      {tags.map((tag) => {
        const slug = categorySlugForTag(tag);
        return (
          <TagPill
            key={tag}
            label={tag}
            href={slug ? ejHealthCategoryRoute(slug) : ejHealthTagRoute(tag)}
          />
        );
      })}
    </div>
  );
}

/** Pill tag used in the 相關主題 cloud — fills with primary on hover.
 *  `href` is required: it defaulted to `"#"` at every call site, so a topic a
 *  reader could see was one they could not open. Reach it through `TagCloud`,
 *  which is what decides whether a given tag has a listing at all. */
function TagPill({ label, href }: { label: string; href: string }) {
  return (
    <Link
      href={href}
      className={`hkej-tap hkej-tap-self ${TAG_PILL_CLASS}`}
      style={tagPillStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = EJHEALTH_COLORS.primary;
        e.currentTarget.style.color = EJHEALTH_COLORS.textOnDark;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = EJHEALTH_COLORS.primarySoft;
        e.currentTarget.style.color = EJHEALTH_COLORS.primary;
      }}
    >
      {label}
    </Link>
  );
}
